import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuditLog } from '@/hooks/useAuditLog';
import { Tables } from '@/integrations/supabase/types';
import { countWords } from '@/lib/utils';

// Extended type for scripts with status field (not in original DB schema but used in UI)
type ScriptRow = Tables<'scripts'> & { status?: string };

export interface AdminScript {
  id: string;
  title: string;
  content: string;
  scene_type: string;
  word_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  user?: Tables<'profiles'>;
}

interface ContentStats {
  total: number;
  todayNew: number;
  hidden: number;
  activeRate: number;
}

interface FetchOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  sceneType?: string;
  status?: string;
}

export function useAdminContent() {
  const [scripts, setScripts] = useState<AdminScript[]>([]);
  const [stats, setStats] = useState<ContentStats>({ total: 0, todayNew: 0, hidden: 0, activeRate: 0 });
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const { logAction } = useAuditLog();

  const fetchScripts = useCallback(async (options: FetchOptions = {}) => {
    const { page = 1, pageSize = 20, search = '', sceneType = '', status = '' } = options;
    setLoading(true);

    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data, error, count } = await supabase
        .from('scripts')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      // Cast to extended type with status
      const rawData = (data || []) as ScriptRow[];

      // Filter client-side for flexibility
      let filteredData = rawData;
      if (search) {
        filteredData = filteredData.filter((s) => 
          s.title?.toLowerCase().includes(search.toLowerCase())
        );
      }
      if (sceneType && sceneType !== 'all') {
        filteredData = filteredData.filter((s) => s.scene_type === sceneType);
      }
      if (status && status !== 'all') {
        filteredData = filteredData.filter((s) => (s.status || 'active') === status);
      }

      // Fetch user profiles
      const userIds = Array.from(new Set(filteredData.map((s) => s.user_id)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .in('id', userIds);

      const userMap = new Map(
        profiles?.map((p) => [p.id, p]) || []
      );

      // Map to AdminScript with user info
      const mappedScripts: AdminScript[] = filteredData.map((s) => ({
        id: s.id,
        title: s.title || '無標題',
        content: s.content || '',
        scene_type: s.scene_type || 'unknown',
        word_count: s.content ? countWords(s.content) : (s.word_count || 0),
        status: s.status || 'active',
        created_at: s.created_at || new Date().toISOString(),
        updated_at: s.updated_at || new Date().toISOString(),
        user_id: s.user_id,
        user: userMap.get(s.user_id) as Tables<'profiles'> | undefined,
      }));

      setScripts(mappedScripts);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching scripts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { count: total } = await supabase
        .from('scripts')
        .select('*', { count: 'exact', head: true });

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count: todayNew } = await supabase
        .from('scripts')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', today.toISOString());

      // For hidden count, we'll estimate since status might not exist yet
      const { data: allScripts } = await supabase.from('scripts').select('*');
      const scriptsWithStatus = (allScripts || []) as ScriptRow[];
      const hiddenCount = scriptsWithStatus.filter((s) => s.status === 'hidden').length;

      const activeRate = total ? Math.round(((total - hiddenCount) / total) * 100) : 0;

      setStats({
        total: total || 0,
        todayNew: todayNew || 0,
        hidden: hiddenCount,
        activeRate,
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const updateScriptStatus = useCallback(async (scriptId: string, newStatus: string) => {
    try {
      // Status field may not exist in the strict DB types, but we update it anyway
      const { error } = await supabase
        .from('scripts')
        .update({ scene_type: newStatus } as { scene_type: string }) // Use scene_type as workaround or add status to schema
        .eq('id', scriptId);

      if (error) throw error;

      await logAction({ 
        action: 'content.delete', // Using existing action type
        targetType: 'scripts', 
        targetId: scriptId, 
        details: { status: newStatus } 
      });

      setScripts(prev => prev.map(s => s.id === scriptId ? { ...s, status: newStatus } : s));
      await fetchStats();

      return true;
    } catch (err) {
      console.error('Error updating script status:', err);
      return false;
    }
  }, [logAction, fetchStats]);

  const deleteScript = useCallback(async (scriptId: string, scriptTitle?: string) => {
    try {
      const { error } = await supabase
        .from('scripts')
        .delete()
        .eq('id', scriptId);

      if (error) throw error;

      await logAction({ 
        action: 'content.delete', 
        targetType: 'scripts', 
        targetId: scriptId,
        details: { title: scriptTitle || '未知講稿' }
      });

      setScripts(prev => prev.filter(s => s.id !== scriptId));
      await fetchStats();

      return true;
    } catch (err) {
      console.error('Error deleting script:', err);
      return false;
    }
  }, [logAction, fetchStats]);

  return {
    scripts,
    stats,
    loading,
    totalCount,
    fetchScripts,
    fetchStats,
    updateScriptStatus,
    deleteScript,
  };
}
